import type { RefObject } from 'react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import _ from 'lodash'
import {
  Box,
  VStack,
  Input as ChakraInput,
  FormControl as ChakraFormControl,
  FormErrorMessage as ChakraFormErrorMessage,
} from '@chakra-ui/react'
import type { OnChangeValue } from 'react-select'
import { IKReactSelectCreatable } from '../../../../ui-kit'

import type { InputFormProps as Props } from '../../../interfaces'
import {
  getFieldsMapping,
  validateFilter,
} from '../../../../utils/mediaLibrary'
import OperatorMenu from '../OperatorMenu'

const NameInputForm = (props: Props): React.ReactElement => {
  const {
    searchQueryJson,
    validationObject,
    groupId,
    filterId,
    initialFocusRef,
    setSearchQueryJson,
  } = props

  const [dirty, setDirty] = useState<boolean>(false)
  const [localValue, setLocalValue] = useState<string>('')

  const expression = useMemo(
    () => searchQueryJson.filterGroups[groupId]?.expressions[filterId],
    [searchQueryJson, groupId, filterId],
  )

  const error: string | null = useMemo(() => {
    // If field is dirty (user is typing), validate against localValue
    // Only for operators that actually use localValue (single string input)
    if (
      dirty &&
      ['IS', 'IS_NOT', 'STARTS_WITH', 'HAS'].includes(expression.operator)
    ) {
      // Create a temporary expression with localValue for validation
      const tempExpression = {
        ...expression,
        value: localValue,
      }

      // Validate the temporary expression with localValue
      const validation = validateFilter({
        expression: tempExpression,
        customMetadataField: undefined, // Name field doesn't use custom metadata
      })

      return validation.error
    }

    // If not dirty or for operators that don't use localValue, use the normal validation from validationObject
    return validationObject.errors[groupId][filterId]
  }, [validationObject, groupId, filterId, dirty, localValue, expression])

  // Initialize local value from expression
  useEffect(() => {
    // Handle different value types based on operator
    if (['IS', 'IS_NOT', 'HAS', 'STARTS_WITH'].includes(expression.operator)) {
      setLocalValue((expression.value as string) || '')
    } else {
      setLocalValue('')
    }
  }, [expression.value, expression.operator])

  const handleOperatorChange = useCallback(
    (newOperator: string) => {
      const newSearchQueryJson = _.cloneDeep(searchQueryJson)
      newSearchQueryJson.filterGroups[groupId].expressions[filterId].operator =
        newOperator
      newSearchQueryJson.filterGroups[groupId].expressions[filterId].value =
        getFieldsMapping({ fieldName: expression.fieldName }).getInitialValue(
          newOperator,
        )
      setSearchQueryJson(newSearchQueryJson)
    },
    [searchQueryJson, groupId, filterId],
  )

  const handleValueChange = useCallback(
    (newValue: string | string[]) => {
      const newSearchQueryJson = _.cloneDeep(searchQueryJson)
      newSearchQueryJson.filterGroups[groupId].expressions[filterId].value =
        newValue
      setSearchQueryJson(newSearchQueryJson)
    },
    [searchQueryJson, groupId, filterId],
  )

  const handleLocalValueChange = useCallback((newValue: string) => {
    setLocalValue(newValue)
    setDirty(true)
  }, [])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleValueChange(localValue)
        setDirty(false)
        // Reset focus by blurring the input
        e.currentTarget.blur()
      }
    },
    [localValue, handleValueChange],
  )

  const handleBlur = useCallback(() => {
    handleValueChange(localValue)
    setDirty(false)
  }, [localValue, handleValueChange])

  useEffect(() => {
    initialFocusRef.current?.focus()
  }, [initialFocusRef, expression.operator])

  return (
    <ChakraFormControl width="full" isInvalid={error !== null && dirty}>
      <VStack alignItems="start" width="full" spacing="0.5em">
        <VStack alignItems="stretch" width="full" spacing="0.5em">
          <OperatorMenu
            width="full"
            expression={expression}
            handleOperatorChange={handleOperatorChange}
          />

          {['IS', 'IS_NOT', 'STARTS_WITH', 'HAS'].includes(
            expression.operator,
          ) && (
            <ChakraInput
              ref={initialFocusRef as RefObject<HTMLInputElement>}
              width="full"
              id={expression.fieldName}
              value={localValue}
              onChange={e => {
                handleLocalValueChange(e.target.value)
              }}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              bgColor="white"
            />
          )}
          {['IS_ONE_OF', 'IS_NONE_OF'].includes(expression.operator) &&
            Array.isArray(expression.value) && (
              <Box width="full">
                <IKReactSelectCreatable
                  ref={initialFocusRef}
                  variant="compact"
                  placeholder="Add values"
                  isMulti
                  value={
                    expression.value.map(val => ({
                      value: val as string,
                      label: val as string,
                    })) ?? []
                  }
                  onChange={(
                    arr: OnChangeValue<
                      {
                        label: string
                        value: string
                      },
                      true
                    >,
                  ) => {
                    const newVal = arr.map(val => val.value)
                    handleValueChange(newVal)
                    setDirty(true)
                  }}
                />
              </Box>
            )}
        </VStack>

        <ChakraFormErrorMessage maxWidth="full">{error}</ChakraFormErrorMessage>
      </VStack>
    </ChakraFormControl>
  )
}

export default NameInputForm
